'use client';

import type { BlogArticleProps } from '@routeroyal/theme-core';
import { sanitizeHTML } from '@routeroyal/ui-core';
import Image from 'next/image';
import { RouteRoyalBlogCard } from './RouteRoyalBlogCard';

export function RouteRoyalBlogArticle({
  article,
  relatedArticles = [],
  onShare,
  locale = 'en',
}: BlogArticleProps) {
  return (
    <article className="max-w-3xl mx-auto">
      <header className="mb-8">
        {article.category && (
          <span className="inline-block px-3 py-1 text-xs font-semibold text-white bg-blue-900 rounded-md mb-4">
            {article.category}
          </span>
        )}
        <h1 className="text-3xl md:text-4xl lg:text-5xl font-bold mb-4 leading-tight text-gray-900">
          {article.title}
        </h1>
        {article.excerpt && (
          <p className="text-lg text-gray-600 mb-6 leading-relaxed">{article.excerpt}</p>
        )}
        <div className="flex flex-wrap items-center gap-3 text-sm text-gray-600 border-t border-b border-gray-200 py-4">
          <div className="flex items-center gap-2">
            {article.author.avatar && (
              <div className="relative w-10 h-10 rounded-full overflow-hidden">
                <Image src={article.author.avatar} alt={article.author.name} fill className="object-cover" />
              </div>
            )}
            <span className="font-semibold text-gray-900">{article.author.name}</span>
          </div>
          <span>&middot;</span>
          <time dateTime={article.publishedAt}>
            {new Date(article.publishedAt).toLocaleDateString('en-GB', { year: 'numeric', month: 'long', day: 'numeric' })}
          </time>
          {article.readTime && (
            <>
              <span>&middot;</span>
              <span>{article.readTime}</span>
            </>
          )}
          {onShare && (
            <button
              onClick={() => onShare('twitter')}
              className="ml-auto px-3 py-1.5 text-xs font-medium text-white bg-blue-900 rounded-md hover:bg-blue-800 transition-colors"
            >
              Share
            </button>
          )}
        </div>
      </header>

      {article.featuredImage && (
        <div className="relative w-full h-[350px] md:h-[450px] mb-10 rounded-lg overflow-hidden">
          <Image src={article.featuredImage} alt={article.title} fill className="object-cover" priority />
        </div>
      )}

      <div
        className="prose prose-lg max-w-none mb-12 prose-headings:text-gray-900 prose-a:text-blue-700 prose-blockquote:border-l-blue-500"
        dangerouslySetInnerHTML={{ __html: sanitizeHTML(article.content) }}
      />

      {article.tags && article.tags.length > 0 && (
        <div className="flex flex-wrap gap-2 mb-12 pb-10 border-b border-gray-200">
          {article.tags.map((tag) => (
            <span key={tag} className="px-3 py-1 text-sm text-blue-900 bg-blue-50 rounded-md font-medium">
              #{tag}
            </span>
          ))}
        </div>
      )}

      {relatedArticles.length > 0 && (
        <section className="mt-14">
          <h2 className="text-2xl font-bold mb-6 text-gray-900">Related Articles</h2>
          <div className="grid gap-6 md:grid-cols-2 lg:grid-cols-3">
            {relatedArticles.map((related) => (
              <RouteRoyalBlogCard key={related.id} article={related} variant="compact" locale={locale} />
            ))}
          </div>
        </section>
      )}
    </article>
  );
}
